/**
 * "Price per m²" collection filter (flooring).
 *
 * Flooring is sold by the pack, so Shopify's price filter — which is server-side
 * on variant price — can't answer "show me flooring under £30/m²". Product cards
 * already render the figure they display as data-price-per-m2 (pack price ÷ pack
 * coverage, in pence); this applies min/max against it in the browser.
 *
 * The filter is loaded only by main-collection-product-grid, and only when the
 * collection's products carry a pack coverage. The inputs come from
 * snippets/m2-price-facet.liquid and sit inside the facets form so their values
 * ride along into the URL whenever another facet changes — but their own input
 * events are stopped in the capture phase, so typing filters the grid in place
 * instead of round-tripping to the server.
 *
 * Because the match happens client-side, every page of the collection has to be
 * accounted for or the result is a lie. When the filter is active and the
 * collection paginates, every page is fetched once through the section
 * rendering API and cached; only the cards that match are put in the grid, and
 * pagination is hidden while it stands in for the whole collection. Clearing the
 * filter puts the original page back.
 */
(function () {
  const MIN_PARAM = 'm2_gte';
  const MAX_PARAM = 'm2_lte';
  const HIDDEN_CLASS = 'm2-filtered-out';
  const CLOSE_ICON =
    '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-close-small" viewBox="0 0 18 18">' +
    '<path d="M13.5 4.5 4.5 13.5M4.5 4.5l9 9" stroke="currentColor" stroke-width="1.2"></path></svg>';

  const pageCache = new Map();
  const countCache = { signature: null, values: new Map() };
  let originalGrid = null;
  let scheduled = false;
  let observer = null;
  let writing = false;
  let inputTimer = null;
  let pendingInput = null;
  let countOverridden = false;

  /* ---------------------------------------------------------------- state */

  function parseAmount(raw) {
    if (raw === null || raw === undefined) return null;
    const cleaned = String(raw).replace(/[^0-9.]/g, '');
    if (cleaned === '') return null;
    const value = Number(cleaned);
    return Number.isFinite(value) ? value : null;
  }

  function readState() {
    const params = new URLSearchParams(window.location.search);
    return { min: parseAmount(params.get(MIN_PARAM)), max: parseAmount(params.get(MAX_PARAM)) };
  }

  function isActive(state) {
    return state.min !== null || state.max !== null;
  }

  function writeState(state) {
    const params = new URLSearchParams(window.location.search);
    if (state.min === null) params.delete(MIN_PARAM);
    else params.set(MIN_PARAM, String(state.min));
    if (state.max === null) params.delete(MAX_PARAM);
    else params.set(MAX_PARAM, String(state.max));
    const query = params.toString();
    history.replaceState(
      history.state,
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}`
    );
  }

  /**
   * Identifies the server-side result set — the collection plus every facet
   * except ours. Merged pages and the saved page-1 markup are only valid while
   * this is unchanged.
   */
  function signature() {
    const params = new URLSearchParams(window.location.search);
    [MIN_PARAM, MAX_PARAM, 'page', 'section_id'].forEach((key) => params.delete(key));
    params.sort();
    return `${window.location.pathname}?${params.toString()}`;
  }

  function formatMoney(amount) {
    const rounded = Math.round(amount * 100) / 100;
    return `£${rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(2)}`;
  }

  function label(state) {
    if (state.min !== null && state.max !== null) {
      return `${formatMoney(state.min)} – ${formatMoney(state.max)}`;
    }
    if (state.min !== null) return `Over ${formatMoney(state.min)}`;
    return `Under ${formatMoney(state.max)}`;
  }

  /* ------------------------------------------------------------------ DOM */

  function grid() {
    return document.getElementById('product-grid');
  }

  /** Runs DOM writes with the observer muted so we don't react to ourselves. */
  function write(fn) {
    writing = true;
    try {
      fn();
    } finally {
      requestAnimationFrame(() => {
        writing = false;
      });
    }
  }

  function syncInputs(state) {
    document.querySelectorAll('[data-m2-input]').forEach((input) => {
      // Only leave the field the shopper is part-way through typing into alone —
      // once its value is committed every field follows the state again.
      if (input === pendingInput) return;
      const value = input.dataset.m2Input === 'min' ? state.min : state.max;
      input.value = value === null ? '' : String(value);
    });
  }

  function renderPills(state) {
    const text = isActive(state) ? `Price per m²: ${label(state)}` : null;
    // Leave an identical pill alone rather than swapping the node out from under
    // a click — apply() runs on every re-render of the facets.
    const existing = Array.from(document.querySelectorAll('[data-m2-pill]'));
    if (text && existing.length && existing.every((pill) => pill.dataset.m2Pill === text)) return;
    existing.forEach((pill) => pill.remove());
    if (!text) return;

    document.querySelectorAll('.active-facets-desktop, .active-facets-mobile').forEach((container) => {
      const pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'active-facets__button active-facets__button--light';
      pill.dataset.m2Pill = text;
      pill.innerHTML =
        '<span class="active-facets__button-inner button button--tertiary">' +
        text +
        `<span class="svg-wrapper">${CLOSE_ICON}</span>` +
        '<span class="visually-hidden">Clear filter</span>' +
        '</span>';
      // The clear-all button isn't always a direct child, so insert relative to it.
      const clearAll = container.querySelector('.active-facets__button-wrapper');
      if (clearAll) clearAll.insertAdjacentElement('beforebegin', pill);
      else container.appendChild(pill);
    });
  }

  function filterItems(productGrid, state) {
    const items = productGrid.querySelectorAll('.grid__item');
    let visible = 0;

    items.forEach((item) => {
      if (!isActive(state)) {
        item.classList.remove(HIDDEN_CLASS);
        visible += 1;
        return;
      }

      const holder = item.querySelector('[data-price-per-m2]');
      const pence = holder ? Number(holder.dataset.pricePerM2) : NaN;
      const matched = matches(Number.isFinite(pence) ? pence / 100 : null, state);

      item.classList.toggle(HIDDEN_CLASS, !matched);
      if (matched) visible += 1;
    });

    return visible;
  }

  /**
   * Swaps the product count for the number of matches. The untouched count is
   * cached per signature, so a facet change — which re-renders the count
   * server-side — refreshes it rather than restoring a stale figure on clear.
   */
  function updateCount(state, visible) {
    const elements = document.querySelectorAll('#ProductCount, #ProductCountDesktop');

    if (countCache.signature !== signature()) {
      countCache.signature = signature();
      countCache.values.clear();
    }

    if (!isActive(state)) {
      if (!countOverridden) return;
      elements.forEach((element) => {
        if (countCache.values.has(element.id)) element.innerHTML = countCache.values.get(element.id);
      });
      countOverridden = false;
      return;
    }

    elements.forEach((element) => {
      if (!countCache.values.has(element.id)) countCache.values.set(element.id, element.innerHTML);
      element.textContent = `${visible} ${visible === 1 ? 'product' : 'products'}`;
    });
    countOverridden = true;
  }

  function updateEmptyState(productGrid, state, visible) {
    const container = productGrid.closest('.collection') || productGrid.parentElement;
    let message = container.querySelector('[data-m2-empty]');

    if (isActive(state) && visible === 0) {
      if (!message) {
        message = document.createElement('div');
        message.className = 'm2-price-filter__empty title-wrapper center';
        message.setAttribute('data-m2-empty', '');
        message.innerHTML =
          '<h2 class="title title--primary">No products match that price per m².<br>' +
          '<button type="button" class="link underlined-link" data-m2-clear>Clear the price per m² filter</button></h2>';
        container.appendChild(message);
      }
    } else if (message) {
      message.remove();
    }
  }

  function togglePagination(hidden) {
    document.querySelectorAll('#ProductGridContainer .pagination-wrapper').forEach((element) => {
      element.classList.toggle(HIDDEN_CLASS, hidden);
    });
  }

  /* ------------------------------------------------------- page collation */

  function pageUrl(sectionId, page) {
    const params = new URLSearchParams(window.location.search);
    [MIN_PARAM, MAX_PARAM, 'page', 'section_id'].forEach((key) => params.delete(key));
    params.set('section_id', sectionId);
    params.set('page', String(page));
    return `${window.location.pathname}?${params.toString()}`;
  }

  /** Fetches one page of the grid and caches its cards as { html, pounds }. */
  function fetchPage(url) {
    if (pageCache.has(url)) return Promise.resolve(pageCache.get(url));
    return fetch(url)
      .then((response) => response.text())
      .then((text) => {
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const fetchedGrid = parsed.getElementById('product-grid');
        const items = fetchedGrid ? Array.from(fetchedGrid.querySelectorAll('.grid__item')) : [];
        const cards = items.map((item) => {
          const holder = item.querySelector('[data-price-per-m2]');
          const pence = holder ? Number(holder.dataset.pricePerM2) : NaN;
          return { html: item.outerHTML, pounds: Number.isFinite(pence) ? pence / 100 : null };
        });
        pageCache.set(url, cards);
        return cards;
      })
      .catch(() => {
        pageCache.set(url, []);
        return [];
      });
  }

  function matches(pounds, state) {
    return (
      pounds !== null &&
      (state.min === null || pounds >= state.min) &&
      (state.max === null || pounds <= state.max)
    );
  }

  function setLoading(loading) {
    const collection = document.querySelector('#ProductGridContainer .collection');
    if (collection) collection.classList.toggle('loading', loading);
  }

  /**
   * Replaces the grid with the matching cards from every page of the collection.
   * Fetches each page once — the cards stay cached, so narrowing the range after
   * that is instant.
   */
  function renderAllPages(productGrid, state) {
    const total = parseInt(productGrid.dataset.totalPages || '1', 10);
    const sectionId = productGrid.dataset.id;
    if (total <= 1 || !sectionId) return;

    const key = `${signature()}|${state.min}|${state.max}`;
    if (productGrid.dataset.m2Key === key || productGrid.dataset.m2Loading === key) return;

    productGrid.dataset.m2Loading = key;
    const urls = [];
    for (let page = 1; page <= total; page += 1) urls.push(pageUrl(sectionId, page));
    if (!urls.every((url) => pageCache.has(url))) setLoading(true);
    const requests = urls.map(fetchPage);

    Promise.all(requests).then((pages) => {
      // The grid was re-rendered (another facet, a sort) while we were fetching.
      if (grid() !== productGrid || `${signature()}|${state.min}|${state.max}` !== key) {
        delete productGrid.dataset.m2Loading;
        return;
      }
      const html = pages
        .flat()
        .filter((card) => matches(card.pounds, state))
        .map((card) => card.html)
        .join('');
      write(() => {
        setLoading(false);
        productGrid.innerHTML = html;
        productGrid.dataset.m2Key = key;
        delete productGrid.dataset.m2Loading;
        togglePagination(true);
      });
      apply();
    });
  }

  function rememberOriginal(productGrid) {
    if (productGrid.dataset.m2Key) return;
    if (originalGrid && originalGrid.signature === signature() && originalGrid.grid === productGrid) return;
    originalGrid = { signature: signature(), grid: productGrid, html: productGrid.innerHTML };
  }

  function restoreOriginal(productGrid) {
    if (!productGrid.dataset.m2Key) return;
    if (!originalGrid || originalGrid.signature !== signature()) return;
    write(() => {
      productGrid.innerHTML = originalGrid.html;
      delete productGrid.dataset.m2Key;
      togglePagination(false);
    });
  }

  /* ----------------------------------------------------------------- main */

  function apply() {
    const state = readState();
    const productGrid = grid();

    write(() => {
      syncInputs(state);
      renderPills(state);
    });

    if (!productGrid) {
      updateCount(state, 0);
      return;
    }

    if (isActive(state)) {
      rememberOriginal(productGrid);
      renderAllPages(productGrid, state);
    } else {
      restoreOriginal(productGrid);
      setLoading(false);
    }

    write(() => {
      const visible = filterItems(productGrid, state);
      updateCount(state, visible);
      updateEmptyState(productGrid, state, visible);
    });
  }

  function onInput(event) {
    const input = event.target.closest && event.target.closest('[data-m2-input]');
    if (!input) return;

    // Keep the change away from the facets form, which would otherwise re-fetch
    // the section on every keystroke.
    event.stopPropagation();

    clearTimeout(inputTimer);
    pendingInput = input;
    inputTimer = setTimeout(() => {
      pendingInput = null;
      const state = { min: null, max: null };
      document.querySelectorAll('[data-m2-input]').forEach((field) => {
        const value = parseAmount(field.value);
        if (value === null) return;
        if (field.dataset.m2Input === 'min') state.min = value;
        else state.max = value;
      });
      if (state.min !== null && state.max !== null && state.min > state.max) {
        const swap = state.min;
        state.min = state.max;
        state.max = swap;
      }
      writeState(state);
      apply();
    }, 500);
  }

  function onClick(event) {
    const trigger = event.target.closest && event.target.closest('[data-m2-clear], [data-m2-pill]');
    if (!trigger) return;
    event.preventDefault();
    event.stopPropagation();
    clearTimeout(inputTimer);
    pendingInput = null;
    writeState({ min: null, max: null });
    apply();
  }

  function observe() {
    if (observer) observer.disconnect();
    observer = new MutationObserver(() => {
      if (writing || scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        apply();
      });
    });

    const targets = [document.getElementById('ProductGridContainer'), document.querySelector('.facets-container')];
    targets.forEach((target) => {
      if (target) observer.observe(target, { childList: true, subtree: true });
    });
  }

  function init() {
    document.addEventListener('input', onInput, true);
    document.addEventListener('change', onInput, true);
    document.addEventListener('click', onClick, true);
    window.addEventListener('popstate', () => apply());
    observe();
    apply();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
